import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  class: "flex flex-col gap-2 text-left",
  "data-testid": "coverage-responsible-field"
}
const _hoisted_2 = {
  class: "text-xs text-[#68688D]",
  "data-testid": "coverage-responsible-hint"
}
const _hoisted_3 = ["disabled"]
const _hoisted_4 = {
  key: 1,
  class: "text-xs text-negative",
  "data-testid": "coverage-responsible-load-error"
}

import { computed, ref } from 'vue'
import { useToast } from 'vue-toastification'
import { useCoverageResponsibles } from '@/hooks/useCoverageResponsibles'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { useErrorToast } from '@/composables/useErrorToast'
import { getCoverageErrorMessage } from '@/utils/coverageErrors'
import {
  COVERAGE_NOTE_LABEL,
  COVERAGE_NOTE_MAX_LENGTH,
  COVERAGE_RESPONSIBLE_LABEL
} from '@/utils/coverage'


export default /*#__PURE__*/_defineComponent({
  __name: 'CoverageResponsibleField',
  props: {
    healthUnitId: {},
    responsibleId: {},
    note: {},
    healthUnitName: { default: null },
    fallbackName: { default: null },
    error: { default: null },
    disabled: { type: Boolean, default: false }
  },
  emits: ["update:responsibleId", "update:note", "update:responsibleName"],
  setup(__props: any, { emit }) {

const props = __props;





const toast = useToast()
const { notifyError, notifyErrorMessage } = useErrorToast()
const permissionsStore = usePermissionsStore()
const { selectableItems, isLoading, loadError, isSupported, reload, create } =
  useCoverageResponsibles(() => props.healthUnitId)

const search = ref('')
const isCreating = ref(false)
const trimmedSearch = computed(() => search.value.trim())

const options = computed(() => {
  const list = selectableItems.value.map(({ id, name }) => ({ id, name }))
  const missingCurrent =
    props.responsibleId != null && !list.some((option) => option.id === props.responsibleId)
  if (missingCurrent && props.fallbackName) {
    list.push({ id: props.responsibleId as number, name: `${props.fallbackName} (inativo)` })
  }
  return list
})

// M9-C / Q39: criar inline só para quem tem a permissão de editar a unidade.
const canCreate = computed(() => {
  if (!props.healthUnitId) return false
  return permissionsStore.check({
    permission: AuthorizationPermissions.HealthUnitEditing,
    resource: {
      objectType: AuthorizationResourcesAndSubjects.HealthUnit,
      objectId: props.healthUnitId
    }
  })
})

const canOfferCreate = computed(() => {
  if (!canCreate.value || !isSupported.value || trimmedSearch.value.length < 2) return false
  const needle = trimmedSearch.value.toLocaleLowerCase('pt-BR')
  return !selectableItems.value.some((item) => item.name.toLocaleLowerCase('pt-BR') === needle)
})

const unitSuffix = computed(() =>
  props.healthUnitName ? `da ${props.healthUnitName}` : 'da unidade'
)

const hintText = computed(() => {
  if (!isSupported.value) return 'A lista de responsáveis ainda não está disponível para esta unidade.'
  if (!isLoading.value && !loadError.value && selectableItems.value.length === 0 && !canCreate.value)
    return 'Nenhum responsável ativo nesta unidade. Peça a quem edita a unidade para cadastrar um item.'
  return props.healthUnitName
    ? `Obrigatório. Lista da unidade ${props.healthUnitName}.`
    : 'Obrigatório. Lista da unidade.'
})

function onSelect(value: number | string | null | undefined) {
  const id = value == null || value === '' ? null : Number(value)
  emit('update:responsibleId', id)
  emit('update:responsibleName', options.value.find((option) => option.id === id)?.name ?? null)
}

async function createFromSearch() {
  isCreating.value = true
  try {
    const created = await create(trimmedSearch.value)
    emit('update:responsibleId', created.id)
    emit('update:responsibleName', created.name)
    search.value = ''
    toast.success('Responsável criado na lista da unidade.')
  } catch (error) {
    const coverageMessage = getCoverageErrorMessage(error)
    if (coverageMessage) notifyErrorMessage(coverageMessage)
    else notifyError(error, 'Não foi possível criar o responsável pela cobertura')
  } finally {
    isCreating.value = false
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_textarea = _resolveComponent("v-textarea")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_v_autocomplete, {
      "model-value": _ctx.responsibleId ?? undefined,
      items: options.value,
      "item-title": "name",
      "item-value": "id",
      label: `${_unref(COVERAGE_RESPONSIBLE_LABEL)} *`,
      loading: _unref(isLoading),
      disabled: _ctx.disabled || !_unref(isSupported) || !_ctx.healthUnitId,
      "error-messages": _ctx.error ? [_ctx.error] : [],
      search: search.value,
      "no-data-text": _unref(isLoading) ? 'Carregando...' : 'Nenhum responsável encontrado',
      variant: "outlined",
      color: "primary20",
      density: "comfortable",
      clearable: "",
      "data-testid": "coverage-responsible-select",
      "onUpdate:modelValue": onSelect,
      "onUpdate:search": _cache[0] || (_cache[0] = ($event: any) => (search.value = $event ?? ''))
    }, null, 8, ["model-value", "items", "label", "loading", "disabled", "error-messages", "search", "no-data-text"]),
    _createElementVNode("p", _hoisted_2, _toDisplayString(hintText.value), 1),
    (canOfferCreate.value)
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          type: "button",
          class: "self-start text-sm font-bold text-primary30 hover:underline disabled:opacity-50",
          disabled: isCreating.value,
          "data-testid": "coverage-responsible-create-button",
          onClick: createFromSearch
        }, " Criar \"" + _toDisplayString(trimmedSearch.value) + "\" na lista " + _toDisplayString(unitSuffix.value), 9, _hoisted_3))
      : _createCommentVNode("", true),
    (_unref(loadError))
      ? (_openBlock(), _createElementBlock("p", _hoisted_4, [
          _createTextVNode(_toDisplayString(_unref(loadError)) + " ", 1),
          _createElementVNode("button", {
            type: "button",
            class: "underline font-semibold",
            "data-testid": "coverage-responsible-retry",
            onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(reload) && _unref(reload)(...args)))
          }, " Tentar novamente ")
        ]))
      : _createCommentVNode("", true),
    _createVNode(_component_v_textarea, {
      "model-value": _ctx.note,
      label: _unref(COVERAGE_NOTE_LABEL),
      counter: _unref(COVERAGE_NOTE_MAX_LENGTH),
      maxlength: _unref(COVERAGE_NOTE_MAX_LENGTH),
      rows: "2",
      "auto-grow": "",
      variant: "outlined",
      color: "primary20",
      density: "comfortable",
      disabled: _ctx.disabled,
      "data-testid": "coverage-note-input",
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (emit('update:note', $event ?? '')))
    }, null, 8, ["model-value", "label", "counter", "maxlength", "disabled"])
  ]))
}
}

})